import { Collapse, Group, Stack } from '@mantine/core';
import { useEffect, useState } from 'react';
import i18n from '../../../base-dictionary';
import { Table } from '../../../components/table/table';
import { skyLight } from '../../../styles/design-tokens';
import { Icon } from '../../../ui-library/icon/icon';
import { Text } from '../../../ui-library/typography/typography';
import { getGroupTitle } from '../../tasks-table/tasks-table.helpers';
import { Grouping } from '../../tasks-table/tasks-table.type';
import type { CollapseState, TaskRecord } from '../../tasks-table/tasks-table.type';
import { getTaskTableColumns, sortTaskData } from './task-detail.helpers';

type Props = {
  tasks: TaskRecord[];
  grouping: Grouping | null;
  groups: Record<string, TaskRecord[]>;
  collapseState: CollapseState;
  setCollapseState: React.Dispatch<React.SetStateAction<CollapseState>>;
  onTaskClick?: (task: TaskRecord) => void;
  selectedTaskId?: string;
};

export const TaskTable = ({
  tasks,
  grouping,
  groups,
  collapseState,
  setCollapseState,
  onTaskClick,
  selectedTaskId,
}: Props) => {
  // For ungrouped view
  const [currentPage, setCurrentPage] = useState(1);
  // For grouped view - track page number for each group
  const [groupPages, setGroupPages] = useState<Record<string, number>>({});
  const PAGE_SIZE = 20;

  useEffect(() => {
    if (grouping) {
      setGroupPages((prevPages) => {
        const initialGroupPages: Record<string, number> = {};
        Object.keys(groups).forEach((key) => {
          initialGroupPages[key] = prevPages[key] || 1;
        });
        return initialGroupPages;
      });
    }
  }, [grouping, groups]);

  const handleGroupPageChange = (groupKey: string, page: number) => {
    setGroupPages((prevPages) => ({
      ...prevPages,
      [groupKey]: page,
    }));
  };

  return (
    <>
      {grouping ? (
        <Stack style={{ gap: '16px' }}>
          {Object.entries(groups).map(([key, groupTasks]) => (
            <GroupSection
              key={`${grouping}-${key}`}
              groupKey={key}
              grouping={grouping}
              tasks={groupTasks}
              collapseState={collapseState}
              setCollapseState={setCollapseState}
              onTaskClick={onTaskClick}
              selectedTaskId={selectedTaskId}
              currentPage={groupPages[key] || 1}
              setCurrentPage={(page) => handleGroupPageChange(key, page)}
              pageSize={PAGE_SIZE}
            />
          ))}
        </Stack>
      ) : (
        <Stack>
          {tasks.length > 0 ? (
            <Table<TaskRecord>
              data={tasks}
              columns={getTaskTableColumns()}
              getRowId={(task) => task.id}
              onRowClick={onTaskClick}
              selectedRowId={selectedTaskId}
              stickyHeader
              pageSize={PAGE_SIZE}
              currentPage={currentPage}
              totalItems={tasks.length}
              onPageChange={(page) => setCurrentPage(page)}
              customSort={sortTaskData}
            />
          ) : (
            <Text>{i18n.t('tasks.empty')}</Text>
          )}
        </Stack>
      )}
    </>
  );
};

type GroupSectionProps = {
  groupKey: string;
  grouping: Grouping;
  tasks: TaskRecord[];
  collapseState: CollapseState;
  setCollapseState: React.Dispatch<React.SetStateAction<CollapseState>>;
  onTaskClick?: (task: TaskRecord) => void;
  selectedTaskId?: string;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
};

const GroupSection = ({
  groupKey,
  grouping,
  tasks,
  collapseState,
  setCollapseState,
  onTaskClick,
  selectedTaskId,
  currentPage,
  setCurrentPage,
  pageSize,
}: GroupSectionProps) => {
  const groupCollapsed = collapseState[groupKey];

  return (
    <div>
      <div
        style={{
          backgroundColor: skyLight,
          borderRadius: 4,
          padding: '12px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
        }}
        onClick={() => {
          setCollapseState({
            ...collapseState,
            [groupKey]: !groupCollapsed,
          });
        }}
      >
        <Text weight="medium">{getGroupTitle(grouping, groupKey)}</Text>
        <Group align="center" gap="sm">
          <Text size="small">{`${i18n.t('common.work_units')}: ${tasks.length}`}</Text>
          <Icon name={groupCollapsed ? 'arrow_drop_down' : 'arrow_drop_up'} />
        </Group>
      </div>
      <Collapse in={!groupCollapsed}>
        <div style={{ marginTop: 8 }}>
          <Table<TaskRecord>
            data={tasks}
            columns={getTaskTableColumns()}
            getRowId={(task) => task.id}
            onRowClick={onTaskClick}
            selectedRowId={selectedTaskId}
            stickyHeader
            pageSize={pageSize}
            currentPage={currentPage}
            totalItems={tasks.length}
            onPageChange={setCurrentPage}
            customSort={sortTaskData}
          />
        </div>
      </Collapse>
    </div>
  );
};
